import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { getGlossaryTerm, GLOSSARY, type GlossaryTerm } from "@/content/glossary";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";

import { abMeta } from "@/content/metaExperiments";
import { GlossaryCrossLinks } from "@/components/glossary/GlossaryCrossLinks";

export const Route = createFileRoute("/glossary/$slug")({
  loader: async ({ params }) => {
    const { getGlossaryTerm } = await import("@/content/glossary");
    const term = getGlossaryTerm(params.slug);
    if (!term) throw notFound();
    return { term };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: `Term not found · ${siteConfig.name}` }, { name: "robots", content: "noindex" }] };
    }
    const { term } = loaderData;
    const title = term.metaTitle ? `${term.metaTitle} · ${siteConfig.name}` : `${term.term} — SEO Glossary · ${siteConfig.name}`;
    const path = `/glossary/${params.slug}/`;
    const ab = abMeta(path, { title, description: term.metaDescription ?? term.short });
    const base = pageMeta({
      path,
      title: ab.title,
      description: ab.description,
      type: "article",
      keywords: [term.term, term.category, "SEO glossary", `${siteConfig.contact.address.city} SEO`, "AI search"],
    });
    const definedTerm = {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: term.term,
      description: term.short,
      inDefinedTermSet: { "@type": "DefinedTermSet", name: `${siteConfig.name} SEO Glossary` },
      url: path,
    };
    const extras = term.faqs?.length ? [faqLd(term.faqs)] : [];
    return {
      ...base,
      scripts: ldScripts(
        definedTerm,
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Glossary", path: "/glossary/" },
          { name: term.term, path },
        ]),
        ...extras,
      ),
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-32">
      <h1 className="font-display text-4xl">Term not found</h1>
      <Link to="/glossary/" className="mt-4 inline-block text-primary">← Back to glossary</Link>
    </div>
  ),
  component: TermPage,
});

function TermPage() {
  const { term } = Route.useLoaderData() as { term: GlossaryTerm };
  const seeAlso = (term.seeAlso ?? []).map(getGlossaryTerm).filter(Boolean) as NonNullable<ReturnType<typeof getGlossaryTerm>>[];

  // Prev/Next A–Z
  const sorted = [...GLOSSARY].sort((a, b) => a.term.localeCompare(b.term));
  const idx = sorted.findIndex((t) => t.slug === term.slug);
  const prev = sorted[idx - 1];
  const next = sorted[idx + 1];

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <div className="container-page pt-8 text-xs uppercase tracking-widest text-muted-foreground">
          <Link to="/resources/" className="hover:text-foreground">Resources</Link>
          <span className="mx-2">/</span>
          <Link to="/glossary/" className="hover:text-foreground">Glossary</Link>
        </div>

        <section className="container-page pt-8 pb-16 max-w-3xl">
          <EyebrowLabel>{term.category}</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl tracking-tight leading-[0.95]">
            {term.term}
          </h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">{term.short}</p>

          <div className="mt-12 space-y-5">
            {term.body.map((p, i) => (
              <p key={i} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
            ))}
          </div>

          {(term.sections ?? []).map((s, i) => (
            <div key={i} className="mt-14">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">{s.heading}</h2>
              <div className="mt-5 space-y-5">
                {s.body.map((p, j) => (
                  <p key={j} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                ))}
              </div>
              {s.bullets && s.bullets.length > 0 && (
                <ul className="mt-6 space-y-3">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex gap-3 text-[1.02rem] leading-relaxed text-foreground/85">
                      <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {(term.benchmarks ?? []).map((b, i) => (
            <div key={i} className="mt-14">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">{b.heading}</h2>
              <div className="mt-6 overflow-hidden rounded-2xl border border-border">
                <table className="w-full text-left text-sm">
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j} className="border-b border-border last:border-0">
                        <th scope="row" className="px-4 py-3 align-top font-medium text-foreground whitespace-nowrap">{r.label}</th>
                        <td className="px-4 py-3 align-top font-display text-primary whitespace-nowrap">{r.value}</td>
                        <td className="px-4 py-3 align-top text-muted-foreground leading-relaxed">{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {term.faqs && term.faqs.length > 0 && (
            <div className="mt-16">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">Frequently asked questions</h2>
              <dl className="mt-6 space-y-6">
                {term.faqs.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-border p-6">
                    <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                    <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {term.example && (
            <div className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6">
              <div className="text-xs uppercase tracking-widest text-primary">Example</div>
              <p className="mt-2 text-foreground/90 leading-relaxed">{term.example}</p>
            </div>
          )}

          <GlossaryCrossLinks slug={term.slug} term={term.term} />


          {seeAlso.length > 0 && (
            <div className="mt-12">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">See also</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {seeAlso.map((s) => (
                  <Link key={s.slug} to="/glossary/$slug/" params={{ slug: s.slug }} className="rounded-full border border-border px-4 py-1.5 text-sm text-foreground/85 hover:border-primary/50 hover:text-foreground transition-colors">
                    {s.term}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-14 grid grid-cols-2 gap-4 pt-8 border-t border-border">
            {prev ? (
              <Link to="/glossary/$slug/" params={{ slug: prev.slug }} className="rounded-xl border border-border p-4 hover:border-primary/40 transition-colors">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">← Previous</div>
                <div className="mt-1 font-display text-base">{prev.term}</div>
              </Link>
            ) : <div />}
            {next ? (
              <Link to="/glossary/$slug/" params={{ slug: next.slug }} className="rounded-xl border border-border p-4 hover:border-primary/40 transition-colors text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Next →</div>
                <div className="mt-1 font-display text-base">{next.term}</div>
              </Link>
            ) : <div />}
          </div>

          <div className="mt-14 rounded-3xl border border-border p-8 bg-gradient-to-br from-primary/10 to-transparent">
            <h3 className="font-display text-2xl tracking-tight">Need this applied to your site?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              We turn concepts like these into shipped work every week.
            </p>
            <div className="mt-5"><ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any} size="md">Start a project <span aria-hidden>→</span></ButtonLink></div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
